import React, { useRef, useState } from 'react'
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import { loading } from '../../actions/Loading';
import api from '../../api/api';
import { useDispatch } from 'react-redux';
import { List } from '../../model/List';
import SortableTable from '../table/SortableTable';
import AnimatedSaveButton from '../AnimatedSaveButton';
import AlertEx from '../AlertEx';
import ConfirmEx from '../ConfirmEx';
import { ContactListAction } from '../../model/UpdateContactListRequest';
import moment from 'moment'

interface IProps {
    stockId: string;
}

const Style = styled.div`
margin: 1rem 0;

.flex {
    display: flex;
    justify-content: space-between;
}
table {
    margin-top: 0.5rem;
}
.sample-file {
    font-size:0.65rem;
    font-style: italic;
}
.tag-segmentation-notice {
    background: #fff8e1;
    border: 1px solid #ffe58f;
    border-radius: 4px;
    padding: 0.6rem 0.85rem;
    margin: 0.5rem 0;
    font-size: 0.85rem;
    line-height: 1.45;
    color: #5c4b00;
}
.tag-segmentation-notice .detail {
    margin-top: 0.25rem;
    padding-left: 0.5rem;
}
.tag-segmentation-notice code {
    background: #fbe9a6;
    padding: 0 0.25rem;
    border-radius: 3px;
}

`;

// Tags written by the add-subscriber widget that activate per-campaign send segmentation.
const SEGMENTATION_TAGS = ['announcements', 'other-news'];

const getHeaders = () =>  {
    return [ 'Email', 'Name', 'Status', 'Tags', 'Created' ];
}

const ContactComponent: React.FC<IProps> = (props: IProps) => {

    const dispatch = useDispatch();
    const [ item, setItem ] = useState<List>();
    const [ canUpload, setCanUpload ] = useState<boolean>(false);
    const [ openActionRow, setOpenActionRow ] = useState<number | null>(null);


    const fileInput = React.createRef<any>();
    const contactCsvFile = useRef<FileList | null>(null);
    const onFileChange = (selectorFiles: FileList|null) =>   {
        contactCsvFile.current = selectorFiles;
        setCanUpload(contactCsvFile != null && contactCsvFile.current != null && contactCsvFile.current.length > 0);
    }     


    const uploadCsv = async () : Promise<boolean> => {
        let file = contactCsvFile!.current![0];
        const result = await api().uploadContactCsv({
            file,
            stockId: props.stockId
        });
        if (result.errorMessage) {
            AlertEx(`${result.errorMessage}`);
        } else {
            const result = await api().getContactList(props.stockId);
            setItem(result);
        }
        return true;
    }

    const addSubscriber = async () => {
        const email = prompt('Enter subscriber email address:');
        if (!email || !email.trim()) {
            return;
        }

        const name = prompt('Enter subscriber name:');
        if (!name || !name.trim()) {
            return;
        }

        // Split name into first and last (first word = first name, rest = last name)
        const nameParts = name.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        // Create a CSV with the email and name
        const csvContent = `Email,LastName,FirstName,Status\n${email.trim()},${lastName},${firstName},subscribed`;
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const file = new File([blob], 'subscriber.csv', { type: 'text/csv' });

        // Create a FileList-like object
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        contactCsvFile.current = dataTransfer.files;

        // Upload the CSV
        await uploadCsv();

        // Clear the file input
        contactCsvFile.current = null;
        setCanUpload(false);
    }

    const updateContacts = async (email: string, action: ContactListAction) => {
        const result = await api().updateContactList({
            stockId: props.stockId,
            action,
            contacts: [{ email }]
        });
        if (result.errorMessage) {
            AlertEx(`${result.errorMessage}`);
            return;
        }
        const list = await api().getContactList(props.stockId);
        setItem(list);
    }

    const markAsUnsubscribed = async (email: string) => {
        if (!confirm(`Mark ${email} as unsubscribed?`)) {
            return;
        }
        await updateContacts(email, ContactListAction.Unsubscribe);
    }

    const deleteContact = async (email: string) => {
        if (await ConfirmEx(`Delete ${email}? This cannot be undone.`)) {
            await updateContacts(email, ContactListAction.Delete);
        }
    }

    useAsyncEffect(async (isMounted:()=>boolean)=> {
        
        dispatch(loading(true));
        const result = await api().getContactList(props.stockId);
        if(isMounted()){
            setItem(result)   
        }
        dispatch(loading(false));
        
        
      }, []);

      const data:any[][] = [];
      let subscribedCount = 0;
      if(item){
          for(let i = 0; i < item.subscribers.length; i++){
              const subscriber = item.subscribers[i];
              let arr:any[] = []

              arr = [
                  subscriber.email,
                  subscriber.name,
                  subscriber.status,
                  (subscriber.tags && subscriber.tags.length ? subscriber.tags.join(', ') : ''),
                  moment(subscriber.created,).format('DD MMM YY'),
                  subscriber  // Add the full model object as the last element
              ]


              data.push(arr)
          }
          subscribedCount = item.subscribers.filter(x=>x.status==='subscribed').length
      }

      const presentSegmentationTags = item
          ? SEGMENTATION_TAGS.filter(tag => item.subscribers.some(s => s.tags?.some(t => t.toLowerCase() === tag)))
          : [];

      const cellFormatter = (columnName: string, cellData: any, subscriber: any) => {
          // Status column with action
          if (columnName === 'Status') {
              const rowIndex = data.findIndex(row => row[5] === subscriber);
              const isOpen = openActionRow === rowIndex;

              return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
                      <span>{cellData}</span>
                      <a
                          href="#"
                          onClick={(e) => {
                              e.preventDefault();
                              setOpenActionRow(isOpen ? null : rowIndex);
                          }}
                          style={{ cursor: 'pointer', fontWeight: 'bold' }}
                      >
                          &gt;&gt;
                      </a>
                      {isOpen && (
                          <div style={{
                              position: 'absolute',
                              left: '0',
                              top: '100%',
                              marginTop: '0.25rem',
                              whiteSpace: 'nowrap',
                              backgroundColor: 'white',
                              border: '1px solid #ccc',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '3px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              zIndex: 1000,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.25rem'
                          }}>
                              {subscriber?.status === 'subscribed' && (
                                  <a
                                      href="#"
                                      onClick={(e) => {
                                          e.preventDefault();
                                          markAsUnsubscribed(subscriber.email);
                                          setOpenActionRow(null);
                                      }}
                                  >
                                      Mark as Unsubscribed
                                  </a>
                              )}
                              <a
                                  href="#"
                                  onClick={(e) => {
                                      e.preventDefault();
                                      deleteContact(subscriber.email);
                                      setOpenActionRow(null);
                                  }}
                                  style={{ color: '#c0392b' }}
                              >
                                  Delete
                              </a>
                          </div>
                      )}
                  </div>
              );
          }
          return null;
      }

    const downloadCSV = () => {
  const csvContent = `Email,LastName,FirstName,Status,Tags
john.smith@example.com,Smith,John,subscribed,
investor1@example.com,Johnson,Sarah,subscribed,linkedin;newsletter`;

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'subscribers.csv';
  
  link.click();
  
  window.URL.revokeObjectURL(url);
};

    return <Style >
        <div className='flex'>
      <h2>Contacts</h2>
            <div>
                <div className="form-item">
                    { !canUpload && <label htmlFor="">Select Contact CSV file</label>}

                    { canUpload && <AnimatedSaveButton onClick={uploadCsv} startingText="Start Upload" progressText="Uploading..." completedText="Uploaded!"  />}
                    <input type="file" accept=".csv"
                        ref={fileInput}
                        onChange={(e) => onFileChange(e.target.files)}
                    />
                    <button className='button-sm link button-as-link sample-file' onClick={downloadCSV}>Download Sample CSV</button>
                </div>

            </div>


            </div>
        { !!item && <>
        { presentSegmentationTags.length > 0 && (
            <div className='tag-segmentation-notice'>
                <strong>Email-alert segmentation is active for this list.</strong>
                <div className='detail'>ASX announcement emails are sent only to subscribers tagged <code>announcements</code>,</div>
                <div className='detail'>News/Other emails only to those tagged <code>other-news</code>.</div>
                <div className='detail'>Subscribers without the matching tag are excluded from that send.</div>
            </div>
        )}
        <div className='flex'>
            <div>{ subscribedCount } subscribed of { item.subscribers.length } total</div>
            <div>
                <button className='button-sm' onClick={addSubscriber}>Add Subscriber</button>
            </div>
        </div>
        <p> </p>
        <SortableTable headers={getHeaders()} data={data} cellFormatter={cellFormatter}  />
        </>}
    </Style>
}

export default ContactComponent;